import { useEffect, useState } from "react";
import { X, Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { getCookieConsent } from "./CookieConsent";

const KEY = "met_newsletter_popup";
const SHOW_DELAY_MS = 18000;

const CONSENT_TEXT =
    "I consent to the use of my details by Whispering Earth LLP, its subsidiaries and partners to contact me for marketing and service purposes — via email, phone and WhatsApp. I can withdraw consent or unsubscribe at any time.";

export const NewsletterPopup = () => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "" });
    const [consent, setConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        try { if (localStorage.getItem(KEY)) return; } catch { return; }
        // Wait for the delay AND for the cookie banner to be answered so the
        // two consent surfaces never stack on top of each other.
        let timer = null;
        const arm = () => {
            timer = setTimeout(() => {
                if (getCookieConsent()) { setOpen(true); return; }
                const poll = setInterval(() => {
                    if (getCookieConsent()) { clearInterval(poll); setOpen(true); }
                }, 1500);
                timer = poll;
            }, SHOW_DELAY_MS);
        };
        arm();
        // Exit intent (desktop): open immediately when the cursor leaves the top
        // of the viewport, if the cookie banner has been answered.
        const onMouseOut = (e) => {
            if (e.clientY > 0 || e.relatedTarget) return;
            try { if (localStorage.getItem(KEY)) return; } catch { return; }
            if (!getCookieConsent()) return;
            setOpen(true);
            document.removeEventListener("mouseout", onMouseOut);
        };
        document.addEventListener("mouseout", onMouseOut);
        return () => {
            clearTimeout(timer);
            clearInterval(timer);
            document.removeEventListener("mouseout", onMouseOut);
        };
    }, []);

    const dismiss = () => {
        try { localStorage.setItem(KEY, "dismissed"); } catch { /* noop */ }
        setOpen(false);
    };

    const update = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.email.trim() || !form.email.includes("@")) {
            toast.error("Please enter a valid email address.");
            return;
        }
        if (!consent) {
            toast.error("Please tick the consent box so we can stay in touch.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/newsletter/subscribe", {
                email: form.email.trim(),
                name: form.name.trim(),
                phone: form.phone.trim(),
                consent_marketing: true,
                source_path: `popup:${window.location.pathname}`,
            });
            setDone(true);
            try { localStorage.setItem(KEY, "subscribed"); } catch { /* noop */ }
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not subscribe. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="newsletter-popup">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={dismiss} aria-hidden="true" />
            <div className="relative w-full max-w-md bg-background border border-border shadow-2xl animate-in fade-in zoom-in-95 duration-300">
                <button
                    type="button"
                    onClick={dismiss}
                    aria-label="Close"
                    data-testid="popup-close"
                    className="absolute top-3 right-3 p-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                    <X size={18} strokeWidth={1.5} />
                </button>

                {done ? (
                    <div className="p-10 text-center">
                        <CheckCircle2 size={40} strokeWidth={1.25} className="text-accent mx-auto mb-5" />
                        <h3 className="font-serif text-2xl tracking-tight mb-3">Welcome aboard.</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            Your interactive flipbook guide is on its way to your inbox — one considered letter a season, no noise.
                        </p>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            data-testid="popup-done-close"
                            className="mt-8 px-8 py-3 bg-primary text-primary-foreground text-[10px] tracking-[0.25em] uppercase font-semibold hover:bg-accent transition-colors"
                        >
                            Continue exploring
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="p-8 sm:p-10" noValidate>
                        <p className="overline text-accent mb-3">The slow letter</p>
                        <h3 className="font-serif text-2xl sm:text-3xl tracking-tight leading-tight mb-3">
                            Receive our free eco-luxury travel guide.
                        </h3>
                        <p className="text-xs text-muted-foreground leading-relaxed mb-6">
                            One short, considered letter a season — plus instant access to our interactive flipbook
                            guide of every destination, experience and journey.
                        </p>

                        <div className="space-y-4">
                            <input
                                type="text"
                                autoComplete="name"
                                value={form.name}
                                onChange={update("name")}
                                placeholder="Your name"
                                data-testid="popup-name"
                                className="w-full bg-transparent border-b border-foreground/20 py-2.5 text-sm focus:outline-none focus:border-primary placeholder:text-muted-foreground"
                            />
                            <input
                                type="email"
                                required
                                autoComplete="email"
                                value={form.email}
                                onChange={update("email")}
                                placeholder="you@example.com *"
                                data-testid="popup-email"
                                className="w-full bg-transparent border-b border-foreground/20 py-2.5 text-sm focus:outline-none focus:border-primary placeholder:text-muted-foreground"
                            />
                            <input
                                type="tel"
                                inputMode="tel"
                                autoComplete="tel"
                                value={form.phone}
                                onChange={update("phone")}
                                placeholder="Mobile number (with country code)"
                                data-testid="popup-phone"
                                className="w-full bg-transparent border-b border-foreground/20 py-2.5 text-sm focus:outline-none focus:border-primary placeholder:text-muted-foreground"
                            />
                        </div>

                        <label className="mt-5 flex items-start gap-3 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={consent}
                                onChange={(e) => setConsent(e.target.checked)}
                                data-testid="popup-consent"
                                className="mt-0.5 h-4 w-4 shrink-0 accent-[#c9a44c]"
                            />
                            <span className="text-[10px] leading-relaxed text-muted-foreground">{CONSENT_TEXT}</span>
                        </label>

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="popup-submit"
                            className="mt-6 w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-primary text-primary-foreground text-xs tracking-[0.25em] uppercase font-semibold hover:bg-accent transition-colors disabled:opacity-60"
                        >
                            {submitting ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} strokeWidth={1.5} />}
                            {submitting ? "Subscribing…" : "Get the guide"}
                        </button>
                        <p className="mt-3 text-[10px] text-muted-foreground text-center">
                            No re-sale, no spam. Unsubscribe in a single click.
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
};
